export enum UserRole {
    HOSPITAL = 'hospital',
    DONOR = 'donor',
    CMRF_DEPARTMENT = 'cmrf_department',
    GOVERNMENT_USER = 'government_user',
    CSR_CELL = 'csr_cell',
}

export enum BeneficiaryStatus {
    PRE_AUTH_PENDING = 'pre-auth-pending',
    PRE_AUTH_DONE = 'pre-auth-done',
    POST_AUTH_PENDING = 'post-auth-pending',
    POST_AUTH_DONE = 'post-auth-done',
    DISBURSEMENT_DONE_BY_DONOR = 'disbursement-done-by-donor',
    DISBURSEMENT_DONE_BY_CMRF = 'disbursement-done-by-cmrf',
    PATIENT_BACKOUT = 'patient-backout',
    PATIENT_GONE_EARLY = 'patient-gone-early',
    NEED_MORE_HELP = 'need-more-help',
    COMPLETE = 'complete',
    NEED_ATTENTION = 'need-attention',
    POST_AUTH_NEED_ATTENTION = 'post-auth-need-attention',
}

export enum CollabAction {
    CREATE_BENEFICIARY = 'create-beneficiary',
    DOCUMENT_VERIFICATION = 'document-verification',
    APPROVE = 'approve',
    REJECT = 'reject',
    STATUS_UPDATE = 'status-update',
    ADD_DISBURSEMENT = 'add-disbursement',
    REQUEST_TO_CMRF = 'request-to-cmrf',
    ADD_UTILIZATION = 'add-utilization',
}

const VERIFIER_ROLES: ReadonlyArray<string> = [
    UserRole.CMRF_DEPARTMENT,
    UserRole.GOVERNMENT_USER,
    UserRole.CSR_CELL,
];

export interface CanShowActionInput {
    userType: string;
    beneficiaryStatus: string;
    actionType: string;
    allDocumentsApproved?: boolean;
    disbursementCount?: number;
}

type RulePredicate = (ctx: CanShowActionInput) => boolean;

const isVerifierRole = (userType: string) => VERIFIER_ROLES.includes(userType);

const ACTION_RULES: Record<string, RulePredicate> = {
    [CollabAction.CREATE_BENEFICIARY]: ({ userType }) =>
        userType === UserRole.HOSPITAL,

    [CollabAction.DOCUMENT_VERIFICATION]: ({ userType, beneficiaryStatus }) =>
        isVerifierRole(userType) &&
        (beneficiaryStatus === BeneficiaryStatus.PRE_AUTH_PENDING ||
            beneficiaryStatus === BeneficiaryStatus.POST_AUTH_PENDING),

    [CollabAction.APPROVE]: ({ userType, beneficiaryStatus }) =>
        isVerifierRole(userType) &&
        (beneficiaryStatus === BeneficiaryStatus.PRE_AUTH_PENDING ||
            beneficiaryStatus === BeneficiaryStatus.POST_AUTH_PENDING),

    [CollabAction.REJECT]: ({ userType, beneficiaryStatus }) =>
        isVerifierRole(userType) &&
        (beneficiaryStatus === BeneficiaryStatus.PRE_AUTH_PENDING ||
            beneficiaryStatus === BeneficiaryStatus.POST_AUTH_PENDING),

    [CollabAction.STATUS_UPDATE]: ({ userType, beneficiaryStatus }) => {
        if (userType === UserRole.HOSPITAL) {
            return beneficiaryStatus !== BeneficiaryStatus.DISBURSEMENT_DONE_BY_CMRF;
        }
        if (isVerifierRole(userType) && beneficiaryStatus === BeneficiaryStatus.DISBURSEMENT_DONE_BY_CMRF) {
            return true;
        }
        if (isVerifierRole(userType) && beneficiaryStatus === BeneficiaryStatus.PRE_AUTH_PENDING) {
            return true;
        }
        if (isVerifierRole(userType) && beneficiaryStatus === BeneficiaryStatus.POST_AUTH_PENDING) {
            return true;
        }
        return isVerifierRole(userType) &&
            beneficiaryStatus === BeneficiaryStatus.POST_AUTH_DONE;
    },

    [CollabAction.ADD_DISBURSEMENT]: ({ userType, beneficiaryStatus }) => {
        if (userType === UserRole.DONOR && beneficiaryStatus === BeneficiaryStatus.PRE_AUTH_DONE) {
            return true;
        }
        if (userType === UserRole.HOSPITAL && (beneficiaryStatus === BeneficiaryStatus.PRE_AUTH_DONE || beneficiaryStatus === BeneficiaryStatus.POST_AUTH_DONE)) {
            return true;
        }
        if ((userType === UserRole.HOSPITAL || userType === UserRole.DONOR) && beneficiaryStatus !== BeneficiaryStatus.PRE_AUTH_PENDING) {
            return true;
        }
        if (isVerifierRole(userType) && beneficiaryStatus === BeneficiaryStatus.POST_AUTH_DONE) {
            return true;
        }
        if (isVerifierRole(userType) && beneficiaryStatus === BeneficiaryStatus.PRE_AUTH_DONE) {
            return true;
        }
        return false;
    },

    [CollabAction.REQUEST_TO_CMRF]: ({ userType, beneficiaryStatus }) =>
        userType === UserRole.HOSPITAL
        && (beneficiaryStatus === BeneficiaryStatus.PRE_AUTH_DONE
            || beneficiaryStatus === BeneficiaryStatus.DISBURSEMENT_DONE_BY_DONOR
            || beneficiaryStatus === BeneficiaryStatus.NEED_ATTENTION
            || beneficiaryStatus === BeneficiaryStatus.POST_AUTH_NEED_ATTENTION),

    [CollabAction.ADD_UTILIZATION]: ({ userType, disbursementCount }) =>
        userType === UserRole.HOSPITAL && (disbursementCount ?? 0) > 1,
};

export function canShowAction(input: CanShowActionInput): boolean {
    const rule = ACTION_RULES[input.actionType];
    if (!rule) return false;
    return rule(input);
}

export function registerActionRule(actionType: string, predicate: RulePredicate): void {
    ACTION_RULES[actionType] = predicate;
}

export interface BeneficiaryStatusDisplay {
    label: string;
    color: string;
    bg: string;
    muiColor: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}

const titleCase = (s: string) =>
    s.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const WARNING_PALETTE = { color: '#EF6C00', bg: '#FFF3E0', muiColor: 'warning' as const };
const SUCCESS_PALETTE = { color: '#2E7D32', bg: '#E8F5E9', muiColor: 'success' as const };
const ERROR_PALETTE = { color: '#C62828', bg: '#FFEBEE', muiColor: 'error' as const };
const INFO_PALETTE = { color: '#1565C0', bg: '#E3F2FD', muiColor: 'info' as const };
const PURPLE_PALETTE = { color: '#6A1B9A', bg: '#F3E5F5', muiColor: 'secondary' as const };

const STATUS_PALETTE: Record<string, { color: string; bg: string; muiColor: BeneficiaryStatusDisplay['muiColor'] }> = {
    [BeneficiaryStatus.PRE_AUTH_PENDING]: WARNING_PALETTE,
    [BeneficiaryStatus.PRE_AUTH_DONE]: SUCCESS_PALETTE,
    [BeneficiaryStatus.POST_AUTH_PENDING]: WARNING_PALETTE,
    [BeneficiaryStatus.POST_AUTH_DONE]: SUCCESS_PALETTE,
    [BeneficiaryStatus.DISBURSEMENT_DONE_BY_DONOR]: PURPLE_PALETTE,
    [BeneficiaryStatus.DISBURSEMENT_DONE_BY_CMRF]: PURPLE_PALETTE,
    [BeneficiaryStatus.PATIENT_BACKOUT]: ERROR_PALETTE,
    [BeneficiaryStatus.PATIENT_GONE_EARLY]: ERROR_PALETTE,
    [BeneficiaryStatus.NEED_MORE_HELP]: WARNING_PALETTE,
    [BeneficiaryStatus.COMPLETE]: SUCCESS_PALETTE,
    [BeneficiaryStatus.NEED_ATTENTION]: ERROR_PALETTE,
    [BeneficiaryStatus.POST_AUTH_NEED_ATTENTION]: ERROR_PALETTE,
};

const STATIC_LABEL: Record<string, string> = {
    [BeneficiaryStatus.PRE_AUTH_PENDING]: 'Pre-Auth Pending',
    [BeneficiaryStatus.POST_AUTH_PENDING]: 'Post-Auth Pending',
    [BeneficiaryStatus.POST_AUTH_DONE]: 'Post-Auth Done',
    [BeneficiaryStatus.DISBURSEMENT_DONE_BY_DONOR]: 'Disbursement Done by Donor',
    [BeneficiaryStatus.DISBURSEMENT_DONE_BY_CMRF]: 'Disbursement Done by CMRF',
    [BeneficiaryStatus.PATIENT_BACKOUT]: 'Patient Backout',
    [BeneficiaryStatus.PATIENT_GONE_EARLY]: 'Patient Gone Early',
    [BeneficiaryStatus.NEED_MORE_HELP]: 'Need More Help',
    [BeneficiaryStatus.COMPLETE]: 'Complete',
    [BeneficiaryStatus.NEED_ATTENTION]: 'Need Attention',
    [BeneficiaryStatus.POST_AUTH_NEED_ATTENTION]: 'Post Auth Need Attention',
};

export function getBeneficiaryStatusLabel(status: string, _userType: string): string {
    if (status === BeneficiaryStatus.PRE_AUTH_DONE) {
        return `${titleCase(status)}`;
    }
    return STATIC_LABEL[status] || titleCase(status);
}

export function getBeneficiaryStatusDisplay(status: string, userType: string): BeneficiaryStatusDisplay {
    const palette = STATUS_PALETTE[status] || INFO_PALETTE;
    return {
        label: getBeneficiaryStatusLabel(status, userType),
        ...palette,
    };
}
